import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { createEaseOut } from '../util/helpers';
import { AnimationEvent } from '@angular/animations';
import { slide } from './slide.animation';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare class Hammer extends EventTarget {
  constructor(target: EventTarget);
}

export interface AnimationTrigger {
  value: string | number;
  params: { [p: string]: any };
}

@Component({
  selector: 'skm-slider',
  template: `<div class="skm-datepicker-slider" [@slide]="slideTrigger" (@slide.done)="done($event)">
  <ng-content></ng-content>
</div>
<button type="button" class="skm-datepicker-pagination skm-prev" (click)="slide(-1)" [disabled]="prevDisabled">&lsaquo;</button>
<button type="button" class="skm-datepicker-pagination skm-next" (click)="slide(1)" [disabled]="nextDisabled">&rsaquo;</button>
`,
  styles: [`:host{display:block;position:relative;height:100%}.skm-datepicker-slider{display:flex;position:relative;left:-100%;height:100%}.skm-datepicker-pagination{position:absolute;top:0}.skm-datepicker-pagination.skm-prev{left:0}.skm-datepicker-pagination.skm-next{right:0}`, `button{display:block;color:inherit}button:not([disabled]){cursor:pointer}button[disabled]{pointer-events:none}.skm-datepicker-pane{flex-shrink:0;width:100%;display:flex;flex-direction:column;overflow:hidden}.skm-datepicker-week{display:flex}.skm-datepicker-weekday{width:14.28571%}.skm-datepicker-content{flex:1 0 0;display:flex;flex-direction:column}.skm-datepicker-grid{flex:1 0 0;display:flex;flex-wrap:wrap;align-content:flex-start;pointer-events:none}.skm-datepicker-indent{height:16.66667%}.skm-datepicker-cell{display:flex}:host-context(.skm-datepicker-days-view) .skm-datepicker-cell{width:14.28571%;height:16.66667%}:host-context(.skm-datepicker-months-view) .skm-datepicker-cell{width:33.33333%;height:25%}:host-context(.skm-datepicker-years-view) .skm-datepicker-cell{width:20%;height:25%}.skm-datepicker-item{flex:1;pointer-events:auto}`],
  animations: [slide()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() prevDisabled = false;
  @Input() nextDisabled = false;

  @Output() slideDone = new EventEmitter<number>();
  notPanning = true;

  slideTrigger: AnimationTrigger;

  private wrapperWidth = 1;
  private panOffset = 0;
  private lastDoneEventToState: string | number;

  private readonly ngUnsubscribe$ = new Subject();
  private readonly easeOut = createEaseOut(1.3);

  constructor(private hostRef: ElementRef, private cd: ChangeDetectorRef, private zone: NgZone) {
    this.cd.detach();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.changeSlideTrigger();
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const host = this.hostRef.nativeElement;
      fromEvent(host, 'mousedown').pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.startPress());

      if ('Hammer' in window) {
        const hammer = new Hammer(host);

        fromEvent(hammer, 'panstart').pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.startPan());
        fromEvent(hammer, 'panmove').pipe(takeUntil(this.ngUnsubscribe$)).subscribe(e => this.pan(e));
        fromEvent(hammer, 'panend pancancel').pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.endPan());

        fromEvent(hammer, 'swiperight').pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.swipe(-1));
        fromEvent(hammer, 'swipeleft').pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => this.swipe(1));
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  done(event: AnimationEvent): void {
    if (event.toState === this.lastDoneEventToState) {
      // workaround for https://github.com/angular/angular/issues/24084
      return;
    }
    this.lastDoneEventToState = event.toState;
    if (typeof event.toState as string | number === 'number') {
      this.slideDone.emit(event.toState as any);
    }
  }

  slide(direction: number): void {
    this.changeSlideTrigger();
    setTimeout(() => this.changeSlideTrigger(direction as -1 | 1));
  }

  private startPress(): void {
    this.notPanning = true;
  }

  private startPan(): void {
    this.notPanning = false;
    this.wrapperWidth = this.hostRef.nativeElement.offsetWidth;
  }

  private pan(event: any): void {
    const multiplier = (event.deltaX > 0 && this.prevDisabled || event.deltaX < 0 && this.nextDisabled) ? 0.1 : 1;
    const absOffset = Math.abs(event.deltaX / this.wrapperWidth);
    this.panOffset = Math.sign(event.deltaX) * this.easeOut(absOffset);
    this.changeSlideTrigger('panning', this.panOffset * multiplier);
  }

  private endPan(): void {
    if (Math.abs(this.panOffset) > 0.5) {
      this.changeSlideTrigger(-Math.sign(this.panOffset) as -1 | 1);
    } else if (this.slideTrigger.value === 'panning') {
      this.changeSlideTrigger();
    }
  }

  private swipe(direction: number): void {
    this.changeSlideTrigger(direction as -1 | 1);
  }

  private changeSlideTrigger(value: 'panning' | 'idle' | -1 | 1 = 'idle', offset = 0): void {
    this.slideTrigger = {
      value: (value === -1 && this.prevDisabled || value === 1 && this.nextDisabled) ? 'idle' : value,
      params: {x: offset * 100},
    };
    this.cd.detectChanges();
  }

}
